import React, { useState, useEffect } from 'react';
import { MailOutlined, BankOutlined, SafetyCertificateOutlined, CheckCircleFilled, RightOutlined } from '@ant-design/icons';
import { CalendarDays, FileCheck2, TreePalm } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Spinner from '../../components/spinner';
import GreetingIcon from './GreetingIcon';
import { getDashboardWidgetQuery } from '@src/util/query/dashboardLayout.query';

const greetingFor = (hour) => {
  if (hour >= 5 && hour < 12) return { text: 'Good morning', type: 'morning' };
  if (hour >= 12 && hour < 17) return { text: 'Good afternoon', type: 'afternoon' };
  if (hour >= 17 && hour < 21) return { text: 'Good evening', type: 'evening' };
  return { text: 'Welcome', type: 'welcome' };
};

const initialsOf = (emp) => `${emp?.firstname?.[0] || ''}${emp?.lastname?.[0] || ''}`.toUpperCase() || '?';
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const round = (n) => Math.round((Number(n) || 0) * 10) / 10;
const shortDate = (d) => (d ? dayjs(d).format('D MMM') : '');
const span = (from, to) => (shortDate(from) === shortDate(to) ? shortDate(from) : `${shortDate(from)} – ${shortDate(to)}`);
const AllClear = ({ children }) => <div className="dh-clear"><CheckCircleFilled /> {children}</div>;

const unitWord = (n, unit) => `${unit === 'hours' ? 'hour' : 'day'}${n === 1 ? '' : 's'}`;
const whenText = (inDays) => (inDays <= 0 ? 'Today' : inDays === 1 ? 'Tomorrow' : `In ${inDays} days`);

/**
 * The card at the top of the dashboard, about the person signed in: who they are on the left, and
 * on the right their own things - holiday left (or taken this year when it isn't tracked for them),
 * their next time off, documents they still have to sign and where their appraisal is. One query
 * (getDashboardWidget "myDay"); only the modules their company has.
 */
const DashboardHeader = ({ ready = true }) => {
  const navigate = useNavigate();
  const [now, setNow] = useState(dayjs());
  const { authenticatedUserDetail } = useSelector((state) => state.userAuthReducer);

  const emp = authenticatedUserDetail?.employeeDetail?.[0];
  const tenantDetail = authenticatedUserDetail?.tenantDetail?.[0];
  // "ADMIN PERMISSION" is the raw userAccessType fallback for accounts with no named role
  const roleLabel = authenticatedUserDetail?.rolename === 'ADMIN PERMISSION' ? 'Admin' : authenticatedUserDetail?.rolename;
  const greeting = greetingFor(now.hour());
  const myCalendar = `/user/employee-detail/${authenticatedUserDetail?.employeeId || emp?._id}/tab/calendar`;

  useEffect(() => {
    const interval = setInterval(() => setNow(dayjs()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const { data } = useQuery(getDashboardWidgetQuery, { variables: { widget: 'myDay' }, skip: !ready || !emp, fetchPolicy: 'network-only', pollInterval: 300000 });
  const day = data?.getDashboardWidget?.response;
  const holiday = day?.holiday;
  const documents = day?.documents;
  const appraisal = day?.appraisal;
  const unit = holiday?.unit || 'days';
  const holidayPct = holiday?.tracked && holiday.entitlement > 0 ? Math.max(0, Math.min(100, (holiday.remaining / holiday.entitlement) * 100)) : null;
  const showPaperwork = !day || documents || appraisal;
  const showHoliday = !day || holiday;

  if (!emp) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spinner /></div>;
  }

  return (
    <div className="dh-card" data-dashboard-header>
      <div className="dh-identity">
        <div className="dh-avatar">
          {emp?.metadata?.imageURL ? <img src={emp.metadata.imageURL} alt="" /> : <span>{initialsOf(emp)}</span>}
        </div>
        <div className="dh-who">
          <div className="dh-greeting">
            <span>{greeting.text}</span>
            <GreetingIcon type={greeting.type} />
          </div>
          <h1 className="dh-name">{[emp?.firstname, emp?.lastname].filter(Boolean).join(' ')}</h1>
          <div className="dh-chips">
            {roleLabel && <span className="dh-chip role"><SafetyCertificateOutlined /> {roleLabel}</span>}
            {tenantDetail?.legalname && <span className="dh-chip"><BankOutlined /> {tenantDetail.legalname}</span>}
            {emp?.metadata?.email && <span className="dh-chip"><MailOutlined /> {emp.metadata.email}</span>}
          </div>
        </div>
      </div>

      {(showHoliday || showPaperwork) && (
        <div className="dh-day" data-your-day>
          <div className="dh-day-head">
            <span className="dh-day-title">For you</span>
            <span className="dh-day-date">{now.format('dddd D MMMM')} · {now.format('HH:mm')}</span>
          </div>
          <div className="dh-day-cards">
            {showHoliday && (
              <button type="button" className="dh-tile clickable" onClick={() => navigate(myCalendar)} data-day-card="holiday">
                <div className="dh-tile-head"><span className="dh-tile-icon you"><TreePalm size={15} /></span>My holiday<RightOutlined className="dh-tile-go" /></div>
                {!day ? <div className="dh-tile-loading" /> : holiday.tracked ? (
                  <>
                    <div className="dh-tile-big" data-day-value="holiday">{round(holiday.remaining)}<small>{unitWord(holiday.remaining, unit)} left</small></div>
                    {holidayPct !== null && <div className="dh-meter"><span style={{ width: `${holidayPct}%` }} /></div>}
                    <div className="dh-tile-sub">Taken {round(holiday.taken)} · Booked {round(holiday.booked)} · of {round(holiday.entitlement)}</div>
                    {holiday.waitingApproval > 0 && <span className="dh-tag warn">{plural(holiday.waitingApproval, 'request', 'requests')} waiting for approval</span>}
                  </>
                ) : (
                  <>
                    <div className="dh-tile-big" data-day-value="holiday">{round(holiday.takenThisYear || 0)}<small>{unitWord(holiday.takenThisYear || 0, 'days')} off this year</small></div>
                    <div className="dh-tile-sub">Your holiday allowance isn't set up</div>
                    {holiday.waitingApproval > 0 && <span className="dh-tag warn">{plural(holiday.waitingApproval, 'request', 'requests')} waiting for approval</span>}
                  </>
                )}
              </button>
            )}

            {showHoliday && (
              <button type="button" className="dh-tile clickable" onClick={() => navigate(myCalendar)} data-day-card="next">
                <div className="dh-tile-head"><span className="dh-tile-icon todo"><CalendarDays size={15} /></span>Next time off<RightOutlined className="dh-tile-go" /></div>
                {!day ? <div className="dh-tile-loading" /> : holiday.next ? (
                  <>
                    <div className="dh-tile-big" data-day-value="next">{whenText(holiday.next.inDays)}</div>
                    <div className="dh-tile-sub"><b>{span(holiday.next.startDate, holiday.next.endDate)}</b> · {holiday.next.type}{holiday.next.days ? ` · ${round(holiday.next.days)} ${unitWord(holiday.next.days, 'days')}` : ''}</div>
                  </>
                ) : (
                  <>
                    <div className="dh-tile-big muted" data-day-value="next">Nothing booked</div>
                    <div className="dh-tile-sub">{holiday.waitingApproval > 0 ? 'Waiting for your request to be approved' : 'Time for a break? Book it on your calendar.'}</div>
                  </>
                )}
              </button>
            )}

            {showPaperwork && (
              <div className="dh-tile" data-day-card="paperwork">
                <div className="dh-tile-head"><span className="dh-tile-icon inbox"><FileCheck2 size={15} /></span>Documents &amp; appraisal</div>
                {!day ? <div className="dh-tile-loading" /> : (
                  <div className="dh-you-lines">
                    {documents && (documents.toSign > 0
                      ? <span className="warn" data-day-value="documents">{plural(documents.toSign, 'document', 'documents')} to sign{documents.overdue ? ` · ${documents.overdue} overdue` : ''}</span>
                      : <AllClear>All your documents are signed</AllClear>)}
                    {appraisal && (appraisal.none
                      ? <span>No appraisal open</span>
                      : <span data-day-value="appraisal"><b>{appraisal.title}</b>{appraisal.dueDate ? ` · due ${shortDate(appraisal.dueDate)}` : ''}{appraisal.action ? <><br /><em>{appraisal.action}</em></> : null}</span>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHeader;
